import React, { useEffect, useState } from "react";
import { FaBicycle, FaStar, FaMedal, FaCrown } from "react-icons/fa";
import { FiCheckCircle, FiTrendingUp, FiMap, FiAward } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const riderLevels = [
  {
    id: 1,
    title: "Rider Onboarding",
    subtitle: "Level 1",
    description: "Start your journey! Complete first deliveries and learn the ropes.",
    tasks: ["Complete 5 deliveries"],
    perks: ["Basic app access", "Standard support", "Rider Community"],
    icon: <FaBicycle />,
    color: "#3b82f6",
  },
  {
    id: 2,
    title: "Trusted Rider",
    subtitle: "Level 2",
    description: "Consistency pays! Unlock new rewards and better support.",
    tasks: ["Complete 20 deliveries"],
    perks: ["Priority support", "Peak hour bonus", "Giveaway entry"],
    icon: <FaStar />,
    color: "#10b981",
  },
  {
    id: 3,
    title: "Verified Rider",
    subtitle: "Level 3",
    description: "Trusted by buyers! Earn recognition and bigger bonuses.",
    tasks: ["Complete 50 deliveries"],
    perks: ["Higher bonuses", "Early promo access", "VIP Rider Badge"],
    icon: <FaMedal />,
    color: "#f59e0b",
  },
  {
    id: 4,
    title: "Top Rider",
    subtitle: "Level 4",
    description: "The best of the best! Unlock top perks and premium recognition.",
    tasks: ["Complete 200 deliveries"],
    perks: ["Dedicated manager", "Exclusive campaigns", "Earnings boost"],
    icon: <FaCrown />,
    color: "#8b5cf6",
  },
];

export default function RiderLevels() {
  const [userData, setUserData] = useState(null); 
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLevel = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found. Please log in again.");

        const decoded = jwtDecode(token);
        if (!decoded?.id) throw new Error("Invalid token format.");

        const userId = decoded.id;
        const res = await api.updateLevel(userId); 

        if (!res?.data) throw new Error("Invalid response from server.");

        setUserData(res.data); 
      } catch (err) {
        setError("Failed to fetch rider level. Please try again later.");
      }
    };

    fetchLevel();
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6 text-center">
        <div className="bg-red-50 text-red-600 p-4 rounded-2xl font-bold max-w-sm border border-red-100">
          {error}
        </div>
        <button onClick={() => navigate(-1)} className="mt-4 text-gray-500 font-bold flex items-center gap-2">
           <IoMdArrowBack /> Go Back
        </button>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
        <p className="mt-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Syncing Logistics...</p>
      </div>
    );
  }

  const currentLevel = userData.level;

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* 🔝 Sticky Navigation Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700">
          <IoMdArrowBack size={24} />
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Rider Odyssey</h1>
        <div className="w-10"></div>
      </div>

      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {/* 🏍 Dashboard Stats Card */}
        <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 mb-10 flex flex-col sm:flex-row items-center gap-6">
          <div className="w-20 h-20 bg-orange-600 rounded-[2rem] flex items-center justify-center text-white shadow-lg shadow-orange-100 shrink-0">
            <FiTrendingUp size={36} strokeWidth={2.5} />
          </div>
          <div className="text-center sm:text-left grow">
            <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.2em] mb-1">Rider Status</p>
            <h2 className="text-2xl font-black text-gray-900 mb-3">Level {currentLevel} Professional</h2>
            <div className="flex items-center justify-center sm:justify-start gap-4">
               <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                  <FiMap className="text-gray-400" size={14}/>
                  <span className="text-xs font-black text-gray-700">{userData.totalOrders} Trips</span>
               </div>
               <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                  <FiAward className="text-gray-400" size={14}/>
                  <span className="text-xs font-black text-gray-700">Top Tier</span>
               </div>
            </div>
          </div>
        </div>

        {/* 🛣 Roadmap Timeline */}
        <div className="space-y-8 relative">
          {/* Central Connecting Spine */}
          <div className="absolute left-8 top-10 bottom-10 w-1 bg-gray-100 -z-0 rounded-full" />

          {riderLevels.map((level) => {
            const isCurrent = level.id === currentLevel;
            const isCompleted = level.id < currentLevel;

            return (
              <div
                key={level.id}
                className={`relative z-10 p-6 rounded-[2rem] border transition-all duration-500 ${
                  isCurrent 
                  ? "bg-white shadow-2xl shadow-gray-200 border-orange-200 scale-[1.02]" 
                  : "bg-white/50 border-gray-50 opacity-80"
                }`}
              >
                {/* Visual Timeline Marker */}
                <div className={`absolute -left-10 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-4 border-gray-50 z-20 transition-colors duration-500 ${isCurrent ? 'bg-orange-600 animate-pulse' : isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />

                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-2xl text-2xl transition-all ${isCurrent ? 'bg-orange-600 text-white shadow-lg shadow-orange-100 rotate-3' : 'bg-gray-100 text-gray-400'}`}>
                      {level.icon}
                    </div>
                    <div>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isCurrent ? 'text-orange-600' : 'text-gray-400'}`}>
                        {level.subtitle}
                      </p>
                      <h3 className="text-xl font-black text-gray-900 leading-tight">{level.title}</h3>
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="bg-orange-100 text-orange-700 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-tighter">
                      Current Rank
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-500 font-medium mb-6 leading-relaxed">
                  {level.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-2">
                  {/* Goal Section */}
                  <div>
                    <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                       The Mission
                    </h4>
                    <ul className="space-y-2">
                      {level.tasks.map((task, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs font-bold text-gray-600">
                          <FiCheckCircle className={isCurrent ? "text-orange-500" : isCompleted ? "text-green-500" : "text-gray-300"} size={16}/> 
                          {task}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Rewards Section */}
                  <div>
                    <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-3">
                      Rank Rewards
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {level.perks.map((perk, idx) => (
                        <span key={idx} className="bg-gray-50 text-gray-500 text-[10px] font-black px-2 py-1 rounded-lg border border-gray-100">
                          {perk}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Live Progress Metrics */}
                {isCurrent && (
                  <div className="mt-8 pt-6 border-t border-gray-50">
                    <div className="flex justify-between items-end mb-2">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Next Milestone Progress</p>
                        <p className="text-xs font-black text-orange-600">{userData.progress}%</p>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-orange-600 rounded-full transition-all duration-[1.5s] ease-in-out"
                        style={{ width: `${userData.progress}%` }}
                      ></div>
                    </div>
                    <p className="mt-3 text-[10px] font-bold text-gray-400 text-center">
                       Keep delivering to reach the next rank!
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}